import { useDispatch } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { setUserMenuList } from "../application/slices/user.js";

// Layout
// import Layout from "./components/Layout";
import Layout from "./components/layout/index.js";
import ErrorBoundary from "./ErrorBoundary.js";
import NoMatch from "./pages/NoMatch.js";

// Auth
import ForgotPasswordPage from "./pages/auth/ForgotPassword.js";
import AuthLayout from "./pages/auth/index.js";
import LoginPage from "./pages/auth/Login.js";
import SignupPage from "./pages/auth/Signup.js";
import ReferralLandingPage from "./pages/auth/ReferralLandingPage.js";
import ContractorReferralLandingPage from "./pages/auth/ContractorReferralLandingPage.js";
import PartnerJoinLandingPage from "./pages/auth/PartnerJoinLandingPage.js";
import LoginReferralRedirect from "./pages/auth/LoginReferralRedirect.js";
import RegisterReferralRedirect from "./pages/auth/RegisterReferralRedirect.js";
import ReferralBootstrap from "./components/ReferralBootstrap.js";

// *********** [ Account ] ***********
import PrivateRoute from "./components/layout/PrivateRoute.js";
import ProtectedRoute from "./components/layout/ProtectedRoute.js";
import ResetPasswordPage from "./pages/account/ResetPassword.js";
import Home from "./pages/Home.js";
import LandingPage from "./pages/Landing.js";
import Forbidden403 from "./pages/error/Forbidden403.js";

// *********** [ Setting ] ***********
import settingRoutes from "./pages/setting/routes.js";

// *********** [ User Management ] ***********
import userManagementRoutes from "./pages/user-management/routes.js";

// *********** [ Basic Information ] ***********
import basicInformationRoutes from "./pages/basic-information/routes.js";

// *********** [ GV  ] ***********
import gvRoutes from "./pages/graphical-view/routes.js";

// *********** [ MNG ] ***********
import mngRoutes from "./pages/mng/routes.js";

// *********** [ MNG ] ***********
import slnRoutes from "./pages/SLN/routes.js";

// *********** [ EDC - Distribution Company ] ***********
import edcRoutes from "./pages/EDC/routes.js";

import { loaderSimpleGet } from "./middleware/route-provider.js";
import ProfilePage from "./pages/account/Profile.js";
import ContractorRegistrationManagement from "./pages/admin/ContractorRegistrationManagement.js";
import AdminOpsHubPage from "./pages/admin/index.js";
import SystemHealthPage from "./pages/admin/SystemHealthPage.js";
import MissionControlDashboardPage from "./pages/admin/mission-control/MissionControlDashboardPage.js";
import AdminOnlyRoute from "./components/auth/AdminOnlyRoute.js";
import ContractorRegistrationPage from "./pages/contractor-registration/index.js";
import RegistrationSuccessPage from "./pages/contractor-registration/SuccessPage.js";
import SolarCalculator from "./pages/solar-calculator/SolarCalculator.js";
import PublicConsultationPage from "./pages/consultation/PublicConsultationPage.js";
import PublicExhibitionBoothPage from "./pages/exhibition/PublicExhibitionBoothPage.js";
import ContractorMasterPublicVerifyPage from "./pages/public/ContractorMasterPublicVerifyPage.js";

// *********** [ Packages ] ***********
import {
  PackageComparison,
  PackageDetails,
  PackageList,
} from "./pages/packages/index.js";

// *********** [ Suppliers ] ***********
import {
  ProductList,
  ProductManagement,
  SupplierDetails,
  SupplierList,
} from "./pages/suppliers/index.js";

// *********** [ Orders ] ***********
import { OrderCreate, OrderDetails, OrderList } from "./pages/orders/index.js";

// *********** [ Financial ] ***********
import {
  InvoiceList,
  PaymentGateway,
  SettlementRequest,
  Transactions,
  WalletDashboard,
} from "./pages/financial/index.js";

// *********** [ Market Growth / Partner Commission ] ***********
import marketGrowthRoutes from "./pages/SLN/market-growth/routes.js";
import partnerCommissionRoutes from "./pages/partner-commission/routes.js";

const AppRouter = () => {
  const dispatch = useDispatch();

  const router = createBrowserRouter([
    {
      path: "/",
      element: <ReferralBootstrap />,
      errorElement: <ErrorBoundary />,
      children: [
        // AUTH
        {
          path: "/auth",
          element: <AuthLayout />,
          children: [
            {
              path: "login",
              element: <LoginPage />,
            },
            {
              path: "signup",
              element: <SignupPage />,
            },
            {
              path: "signup/:userKind",
              element: <SignupPage />,
            },
            {
              path: "forgot-password",
              element: <ForgotPasswordPage />,
            },
          ],
        },
        {
          path: "/r/:referralCode",
          element: <ReferralLandingPage />,
        },
        {
          path: "/c/:referralCode",
          element: <ContractorReferralLandingPage />,
        },
        {
          path: "/p/:referralCode/join",
          element: <PartnerJoinLandingPage />,
        },
        {
          path: "/login",
          element: <LoginReferralRedirect />,
        },
        {
          path: "/register",
          element: <RegisterReferralRedirect />,
        },
        {
          path: "/register/form",
          element: <AuthLayout />,
          children: [
            {
              index: true,
              element: <SignupPage />,
            },
          ],
        },
        // Landing
        {
          path: "/landing",
          element: <LandingPage />,
        },
        // Contractor Registration (Public Access)
        {
          path: "/contractor-registration",
          element: <ContractorRegistrationPage />,
        },
        {
          path: "/contractor-registration/success",
          element: <RegistrationSuccessPage />,
        },
        // Solar Calculator (Public Access)
        {
          path: "/solar-calculator",
          element: <SolarCalculator />,
        },
        {
          path: "/consultation-request",
          element: <PublicConsultationPage />,
        },
        {
          path: "/exhibition-booth",
          element: <PublicExhibitionBoothPage />,
        },
        {
          path: "/verify-contractor-master/:verificationCode",
          element: <ContractorMasterPublicVerifyPage />,
        },
        // Private Route
        {
          path: "/",
          element: (
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          ),
          // loader حذف شد - menuList در Layout لود می‌شود
          children: [
            {
              path: "403",
              element: <Forbidden403 />,
            },
            {
              path: "",
              element: <Home />,
              // loader: async () => {
              //   return await loaderSimpleGet({
              //     url: "Home/GetData",
              //   });
              // },
            },
            {
              path: "account",
              children: [
                {
                  path: "resetpassword",
                  element: <ResetPasswordPage />,
                },
                {
                  path: "profile",
                  element: <ProfilePage />,
                  loader: async () =>
                    await loaderSimpleGet({
                      url: "Profile/GetCompleteProfile",
                    }),
                },
              ],
            },
            // Admin - Contractor Registration Management
            {
              path: "admin",
              children: [
                {
                  path: "",
                  element: <AdminOpsHubPage />,
                },
                {
                  path: "contractor-registrations",
                  element: <ContractorRegistrationManagement />,
                },
                {
                  path: "system-health",
                  element: (
                    <AdminOnlyRoute>
                      <SystemHealthPage />
                    </AdminOnlyRoute>
                  ),
                },
                {
                  path: "mission-control",
                  element: (
                    <AdminOnlyRoute>
                      <MissionControlDashboardPage />
                    </AdminOnlyRoute>
                  ),
                },
              ],
            },
            // Packages
            {
              path: "packages",
              children: [
                {
                  path: "",
                  element: <PackageList />,
                },
                {
                  path: ":id",
                  element: <PackageDetails />,
                },
                {
                  path: "compare",
                  element: <PackageComparison />,
                },
              ],
            },
            // Suppliers
            {
              path: "suppliers",
              children: [
                {
                  path: "",
                  element: <SupplierList />,
                },
                {
                  path: ":id",
                  element: <SupplierDetails />,
                },
                {
                  path: "my-products",
                  element: <ProductManagement />,
                },
              ],
            },
            // Products
            {
              path: "products",
              element: <ProductList />,
            },
            // Orders
            {
              path: "orders",
              children: [
                {
                  path: "",
                  element: <OrderList />,
                },
                {
                  path: "create",
                  element: <OrderCreate />,
                },
                {
                  path: ":id",
                  element: <OrderDetails />,
                },
              ],
            },
            // Financial
            {
              path: "wallet",
              element: <WalletDashboard />,
            },
            {
              path: "transactions",
              element: <Transactions />,
            },
            {
              path: "settlements",
              element: <SettlementRequest />,
            },
            {
              path: "invoices",
              element: <InvoiceList />,
            },
            {
              path: "payment/:invoiceId",
              element: <PaymentGateway />,
            },
            // UM
            userManagementRoutes,
            // BI - Basic Information
            basicInformationRoutes,
            // // MNG
            // {
            //   path: "MNG",
            //   children: [
            //     {
            //       path: "",
            //       element: <ManagementPage />,
            //     },
            //   ],
            // },

            // Setting
            settingRoutes,
            // GV
            gvRoutes,

            mngRoutes,
            slnRoutes,
            edcRoutes,
            marketGrowthRoutes,
            partnerCommissionRoutes,
            {
              path: "*",
              element: <NoMatch />,
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

export default AppRouter;
